import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateProviderRequest,
  UpdateProviderRequest,
  AssignLocationRequest,
} from '@vagusx/clients';
import { getProviderClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Hook to fetch all active providers.
 */
export function useProviders() {
  return useQuery({
    queryKey: queryKeys.providers.list(),
    queryFn: () => getProviderClient().list(),
  });
}

/**
 * Hook to fetch inactive (deactivated) providers. Loaded eagerly on the
 * providers page so the Inactive tab can always show its count.
 */
export function useInactiveProviders() {
  return useQuery({
    queryKey: queryKeys.providers.inactive(),
    queryFn: () => getProviderClient().listInactive(),
  });
}

/**
 * Hook to fetch a single provider by ID.
 */
export function useProvider(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.providers.detail(id ?? ''),
    queryFn: () => getProviderClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a provider.
 */
export function useCreateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProviderRequest) => getProviderClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
    },
  });
}

/**
 * Hook to update a provider.
 */
export function useUpdateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProviderRequest }) =>
      getProviderClient().update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
    },
  });
}

/**
 * Hook to deactivate a provider (soft-delete — sets row_status = INACTIVE).
 */
export function useDeleteProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getProviderClient().deleteProvider(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
    },
  });
}

/**
 * Hook to reactivate a deactivated provider.
 */
export function useReactivateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getProviderClient().reactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
    },
  });
}

/**
 * Hook to fetch locations for a provider.
 */
export function useProviderLocations(providerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.providers.locations(providerId ?? ''),
    queryFn: () => getProviderClient().getLocations(providerId!),
    enabled: !!providerId,
  });
}

// The provider↔location join row drives both a provider's location list and a
// location's provider list, so every association mutation invalidates both
// trees. (There is no location-side assign endpoint — assigning a provider to a
// location is the same POST /providers/:id/locations from the other direction.)

/**
 * Hook to assign a location to a provider.
 */
export function useAssignProviderLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, data }: { providerId: string; data: AssignLocationRequest }) =>
      getProviderClient().assignLocation(providerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
    },
  });
}

/**
 * Hook to remove a location from a provider.
 */
export function useRemoveProviderLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, locationId }: { providerId: string; locationId: string }) =>
      getProviderClient().removeLocation(providerId, locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
    },
  });
}

/**
 * Hook to set a provider's primary location.
 */
export function useSetPrimaryProviderLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, locationId }: { providerId: string; locationId: string }) =>
      getProviderClient().setPrimaryLocation(providerId, locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
    },
  });
}
